import EmptyFolder from 'assets/img/app/empty-folder.png';
import { IonIcon, IonSpinner } from "@ionic/react";
import { SetStateAction, useEffect, useState } from "react";
import { convertTimestampToDate, getOrderDistance, getSearchFiltersValues } from "utils";
import { Accordion, AccordionDetails, AccordionSummary, Checkbox, FormControlLabel, Pagination } from '@mui/material';
import { IconSeller } from 'assets';
import { collection, getDocs, orderBy, query, where } from "firebase/firestore";
import { FirebaseDB } from 'firebase-config';
import { caretDown } from 'ionicons/icons';
import { SearchFilterItem, SearchFilters } from 'interfaces';

interface SellerRecord {
  key: string;
  vendorName: string;
  sellerCode: string;
  date: string;
  orderNumber: string;
  branchProximity: string;
  showFlagDistance?: boolean;
  totalSold: string;
  nonPurchaseReason: string | null;
  cardName?: string;
}

type CustomerVisitControlFilters = "vendorName";

const initialSearchFilters: Record<CustomerVisitControlFilters, SearchFilters> = {
  vendorName: {
    allChecked: true,
    allHidden: false,
    items: [],
  },
};

export const SellersOrders = (
  {
    showOrdersDetails,
    contentRef,
    today, // no se usa aquí, pero lo dejo por compatibilidad
    startDate,            // ✅ fechas COMMIT del padre (start @00:00)
    setStartDate,         // (no se usa aquí)
    startDateSearchFilter,// (legacy, ya no se usa en este hijo)
    setStartDateSearchFilter,
    finishDate,           // ✅ fechas COMMIT del padre (end @23:59:59.999)
    setFinishDate,        // (no se usa aquí)
    finishDateSearchFilter,
    setFinishDateSearchFilter,
    hasDatesChanged,      // ✅ trigger de refetch
    setHasDatesChanged,
    selectedVendors,
    onChangeSelectedVendors,
  }:{
    showOrdersDetails: boolean,
    contentRef: React.RefObject<HTMLIonContentElement>,
    today: Date,
    startDate: Date,
    setStartDate: React.Dispatch<SetStateAction<Date>>,
    startDateSearchFilter: Date,
    setStartDateSearchFilter: React.Dispatch<SetStateAction<Date>>,
    finishDate: Date,
    setFinishDate: React.Dispatch<SetStateAction<Date>>,
    finishDateSearchFilter: Date,
    setFinishDateSearchFilter: React.Dispatch<SetStateAction<Date>>,
    hasDatesChanged: number,
    setHasDatesChanged: React.Dispatch<SetStateAction<number>>,
    selectedVendors: string[] | null,
    onChangeSelectedVendors: (v: string[] | null) => void,
  }
) => {
  // Data base y filtrada
  const [sellersData, setSellersData] = useState<any[]>([]);
  const [filteredSellersData, setFilteredSellersData] = useState<any[]>([]);

  // Reporte y página visible
  const [report, setReport] = useState<SellerRecord[][]>([]);
  const [visibleReport, setVisibleReport] = useState<SellerRecord[]>([]);

  // Paginación
  const [paginationCount, setPaginationCount] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [recordsPerView] = useState<number>(10);

  // Loading flags
  const [loadingReport, setLoadingReport] = useState<boolean>(true);
  const [filteringReport, setFilteringReport] = useState<boolean>(false);

  // Filtros de UI (solo vendedores)
  const [searchFilters, setSearchFilters] = useState<Record<CustomerVisitControlFilters, SearchFilters>>(initialSearchFilters);

  // ========= Fetch principal  =========
  const getInitialData = async () => {
    const startKey = startDate.getTime();
    const finishKey = finishDate.getTime();

    try {
      setLoadingReport(true);

      const ordersRef = collection(FirebaseDB, "orders");
      const totalOrdersQuery = query(
        ordersRef,
        where("created_at", ">=", startDate),
        where("created_at", "<=", finishDate),
        where("test", "==", false),
        orderBy("created_at", "desc")
      );

      const snap = await getDocs(totalOrdersQuery);
    //       console.log(
    //   `Filtrado de órdenes: startDate=${startDate.toISOString()}, finishDate=${finishDate.toISOString()}`
    // );
      
    // console.log(
    //   "Resultado de la consulta a la base de datos:",
    //   snap.docs.map(doc => doc.data()) // Muestra solo los datos de los documentos
    // );      

      // Guard anti-race: si cambiaron las fechas mientras traíamos datos, abortar
      if (startKey !== startDate.getTime() || finishKey !== finishDate.getTime()) return;

      if (snap.empty) {
        setSellersData([]);
        setFilteredSellersData([]);
        setReport([]); setVisibleReport([]);
        setPaginationCount(0); setCurrentPage(1);
        setSearchFilters({ vendorName: { allChecked: true, allHidden: true, items: [] } });
        return;
      }

      const baseData = snap.docs.map(d => d.data());
      setSellersData(baseData);

      // UI de checkboxes desde la data + selección compartida
      const vendorNames = Array.from(new Set(baseData.map((x: any) => x.vendorName).filter(Boolean)));
      const items = vendorNames.map(v => ({
        value: v, label: v, visible: true,
        checked: selectedVendors ? selectedVendors.includes(v) : true, // null => todos
      }));

      setSearchFilters({
        vendorName: {
          allChecked: items.length > 0 && items.every(i => i.checked),
          allHidden: items.length === 0,
          items,
        },
      });

      // ⚠️ Usar baseData (no sellersData) para evitar "stale"
      const filtered = selectedVendors === null
        ? baseData
        : baseData.filter((row: any) => selectedVendors.includes(row.vendorName));

      setFilteredSellersData(filtered);
      setPaginationCount(Math.ceil(filtered.length / recordsPerView));
      setCurrentPage(1);

      // Primera página
      const firstPage = filtered.slice(0, recordsPerView).map((item: any) => ({
        key: item.$key,
        vendorName: item.vendorName ?? "",
        sellerCode: item.salesPersonCode ?? "",
        date: convertTimestampToDate(item.created_at) ?? "",
        orderNumber: item.numberOrder,
        branchProximity: getOrderDistance(item.trackingDetails),
        showFlagDistance: item.showFlagDistance,
        totalSold: (Number(item.totalIva))?.toLocaleString("es-SV", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        nonPurchaseReason: item.notesTracking?.notes ?? null,
        cardName: item.cardName ?? "",
      }));

      setReport([firstPage]);
      setVisibleReport(firstPage);
    } catch {
      setSellersData([]); setFilteredSellersData([]);
      setReport([]); setVisibleReport([]);
      setPaginationCount(0); setCurrentPage(1);
      setSearchFilters({ vendorName: { allChecked: true, allHidden: true, items: [] } });
    } finally {
      setLoadingReport(false);
    }
  };

  // Refetch al cambiar el commit (trigger del padre)
  useEffect(() => {
    getInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasDatesChanged]);

  // Reconstrucción de filtros y datos visibles al cambiar selección o data
  useEffect(() => {
    const vendorNames = Array.from(new Set(
      sellersData.map((x: any) => x.vendorName).filter(Boolean)
    ));
    const items = vendorNames.map(v => ({
      value: v, label: v, visible: true,
      checked: selectedVendors ? selectedVendors.includes(v) : true,
    }));

    setSearchFilters({
      vendorName: {
        allChecked: items.length > 0 && items.every(i => i.checked),
        allHidden: items.length === 0,
        items,
      },
    });

    const filtered = selectedVendors === null
      ? sellersData
      : sellersData.filter((row: any) => selectedVendors.includes(row.vendorName));

    setFilteredSellersData(filtered);
    setPaginationCount(Math.ceil(filtered.length / recordsPerView));
    setCurrentPage(1);
  }, [selectedVendors, sellersData]);

  // Paginación
  const changePaginationHandler = async (_event: React.ChangeEvent<unknown>, value: number) => {
    setFilteringReport(true);

    let visibleReportAux: SellerRecord[] = [];

    if (report[value - 1] === undefined) {
      const filteredSlice = filteredSellersData.slice(recordsPerView * (value - 1), recordsPerView * value);

      visibleReportAux = filteredSlice.map((item: any) => ({
        key: item.$key,
        vendorName: item.vendorName ?? "",
        sellerCode: item.salesPersonCode ?? "",
        date: convertTimestampToDate(item.created_at) ?? "",
        orderNumber: item.numberOrder,
        branchProximity: getOrderDistance(item.trackingDetails),
        showFlagDistance: item.showFlagDistance,
        totalSold: (Number(item.totalIva))?.toLocaleString("es-SV", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
        nonPurchaseReason: item.notesTracking?.notes ?? null,
        cardName: item.cardName ?? "",
      }));

      setReport(prev => {
        const copy = [...prev];
        copy[value - 1] = visibleReportAux;
        return copy;
      });
    } else {
      visibleReportAux = report[value - 1];
    }

    setVisibleReport(visibleReportAux);
    setCurrentPage(value);

    if (contentRef && contentRef.current) {
      contentRef.current.scrollToTop(500);
    }

    setFilteringReport(false);
  };

  // Recalcular primera página cuando cambie el set filtrado
  useEffect(() => {
    setFilteringReport(true);

    const firstPageItems = filteredSellersData.slice(0, recordsPerView).map((item: any) => ({
      key: item.$key,
      vendorName: item.vendorName ?? "",
      sellerCode: item.salesPersonCode ?? "",
      date: convertTimestampToDate(item.created_at) ?? "",
      orderNumber: item.numberOrder,
      branchProximity: getOrderDistance(item.trackingDetails),
      showFlagDistance: item.showFlagDistance,
      totalSold: (Number(item.totalIva))?.toLocaleString("es-SV", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
      nonPurchaseReason: item.notesTracking?.notes ?? null,
      cardName: item.cardName ?? "",
    }));

    setReport([firstPageItems]);
    setVisibleReport(firstPageItems);
    setCurrentPage(1);
    setFilteringReport(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredSellersData]);

  // ====== Filtros de vendedores ======
  function emitSelectedVendors(sf: Record<CustomerVisitControlFilters, SearchFilters>) {
    const visible = sf.vendorName.items.filter(i => i.visible);
    const checked = visible.filter(i => i.checked).map(i => i.value);
    onChangeSelectedVendors(checked.length === visible.length ? null : checked);
  }

  const allCheckedChangeHandler = () => {
    const isAllChecked = searchFilters.vendorName.allChecked;
    if (isAllChecked) onChangeSelectedVendors([]);
    else onChangeSelectedVendors(null);
  };

  const changeSearchFilterHandler = (event: React.ChangeEvent<HTMLInputElement>, filterName: CustomerVisitControlFilters) => {
    const filterCategory = searchFilters[filterName];
    const filterArray = filterCategory.items;

    const indexFilter = filterArray.findIndex(item => item.value === event.target.value);
    if (indexFilter === -1) return;

    const updatedFilter = { ...filterArray[indexFilter], checked: event.target.checked };

    const updatedFilterArray = [
      ...filterArray.slice(0, indexFilter),
      updatedFilter,
      ...filterArray.slice(indexFilter + 1)
    ];

    const searchFiltersAux = {
      ...searchFilters,
      [filterName]: {
        ...searchFilters[filterName],
        items: updatedFilterArray
      }
    };

    updateFiltersAndBenchmarks(searchFiltersAux, filterName);
    emitSelectedVendors(searchFiltersAux);
  };

  const updateFiltersAndBenchmarks = (searchFiltersAux: Record<CustomerVisitControlFilters, SearchFilters>, filterName: CustomerVisitControlFilters) => {
    const filterCategory = searchFiltersAux[filterName];
    const checkedFilters = filterCategory.items.filter(item => item.checked).map(item => item.value);
    let newFilteredSellersData = sellersData.filter(item => checkedFilters.includes(item[filterName]));

    for (const key in searchFiltersAux) {
      if (key !== filterName) {
        const foundSearchFilters = getSearchFiltersValues(newFilteredSellersData, key as CustomerVisitControlFilters);

        searchFiltersAux[key as CustomerVisitControlFilters].items = searchFiltersAux[key as CustomerVisitControlFilters].items.map(item => ({
          ...item,
          visible: foundSearchFilters.includes(item.value)
        }));
      }

      const allChecked = searchFiltersAux[key as CustomerVisitControlFilters].items.every(item => !item.visible || item.checked);
      const allHidden = searchFiltersAux[key as CustomerVisitControlFilters].items.every(item => !item.visible);

      searchFiltersAux[key as CustomerVisitControlFilters] = {
        ...searchFiltersAux[key as CustomerVisitControlFilters],
        allChecked,
        allHidden,
      };
    }

    const activeFilters = Object.keys(searchFiltersAux).reduce((acc, key) => {
      const filters = searchFiltersAux[key as CustomerVisitControlFilters].items
        .filter(item => item.checked && item.visible)
        .map(item => item.value);
      return { ...acc, [key]: filters };
    }, {} as Record<CustomerVisitControlFilters, string[]>);

    newFilteredSellersData = sellersData.filter(item => {
      return Object.keys(activeFilters).every(key => {
        const filterKey = key as CustomerVisitControlFilters;
        return activeFilters[filterKey].includes(item[filterKey]);
      });
    });

    setSearchFilters(searchFiltersAux);
    setFilteredSellersData(newFilteredSellersData);
    setCurrentPage(1);
    setPaginationCount(Math.ceil(newFilteredSellersData.length / recordsPerView));
  };

  return (
    <>
      <div id="main-section" className={`w-full h-screen overflow-y-scroll grid grid-cols-10 ${!showOrdersDetails && 'hidden'}`}>
        {loadingReport && (
          <div className="col-span-10 w-full h-full flex flex-col items-center justify-center">
            <IonSpinner name="crescent" color="primary" className="w-[50px] h-[50px]" />
          </div>
        )}

        {!loadingReport && (
          <div className="w-full h-full flex flex-col flex-grow col-span-7">
            {filteringReport && (
              <div className="w-full h-screen flex flex-col items-center justify-center">
                <IonSpinner name="crescent" color="primary" className="w-[50px] h-[50px]" />
              </div>
            )}

            {!filteringReport && !(visibleReport.length > 0) && (
              <div className="h-screen grid grid-cols-8">
                <div className="col-span-8 flex flex-col items-center gap-4 p-4">
                  <img src={EmptyFolder} alt='empty' className="h-[200px] object-contain" />
                  <p className="text-xl text-center font-semibold text-primary">No se encontraron resultados para tu búsqueda</p>
                </div>
              </div>
            )}

            {!filteringReport && visibleReport.length > 0 && (
              <div className="w-full h-full flex flex-col">
                <div className="px-4 pt-4 pb-1 grid grid-cols-8 gap-4 text-mid border-b-2 border-tertiary [&>p]:mt-auto [&>p]:text-primary [&>p]:font-semibold">
                  <p>Vendedor</p>
                  <p>Cód. Vendedor</p>
                  <p>Fecha</p>
                  <p>Número de Orden</p>
                  <p>Proximidad de Local</p>
                  <p>Rango Aceptable</p>
                  <p>Total Vendido</p>
                  <p>Cliente</p>
                </div>

                <div className="w-full flex flex-col gap-7">
                  {visibleReport.map((sellerRecord: SellerRecord) => (
                    <div
                      key={sellerRecord.key}
                      className="w-full px-4 py-3 odd:bg-light items-center grid grid-cols-8 gap-4 text-mid"
                    >
                      <div className="w-full flex items-center gap-3">
                        <div className="w-[18px]">
                          <IconSeller />
                        </div>
                        <p>{sellerRecord.vendorName}</p>
                      </div>

                      <p>{sellerRecord.sellerCode}</p>
                      <p>{sellerRecord.date}</p>

                      {sellerRecord.nonPurchaseReason ? (
                        <div className="w-full flex flex-col text-small">
                          <p>Motivo de No Compra</p>
                          <p className="font-bold">{sellerRecord.nonPurchaseReason}</p>
                        </div>
                      ) : (
                        <p>#{sellerRecord.orderNumber}</p>
                      )}

                      <p>{sellerRecord.branchProximity}</p>
                      <p className="text-center">
                        {sellerRecord.showFlagDistance === true
                          ? <span className="text-red-500">❌</span>
                          : <span className="text-green-500">✔️</span>}
                      </p>
                      <p>${sellerRecord.totalSold}</p>
                      <p>{sellerRecord.cardName}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="w-full flex justify-center mt-auto py-4">
              <Pagination
                size="large"
                color="primary"
                count={paginationCount}
                defaultPage={1}
                page={currentPage}
                siblingCount={1}
                boundaryCount={2}
                onChange={changePaginationHandler}
              />
            </div>
          </div>
        )}

        {/* Filtros (solo Vendedores) */}
        {!loadingReport && (
          <div className="w-full overflow-y-scroll flex p-2 flex-col col-span-3 order-last border-l-2 border-light">
            <Accordion className="accordion-search-filter">
              <AccordionSummary
                expandIcon={<IonIcon icon={caretDown} className="text-[18px] text-black" />}
                aria-controls="panel1a-content"
                id="panel1a-header"
              >
                <p className="font-semibold text-mid">Vendedores</p>
              </AccordionSummary>

              <AccordionDetails>
                <div className="flex flex-col">
                  {searchFilters.vendorName.allHidden && (
                    <p className="font-semibold text-primary">No hay datos disponibles</p>
                  )}

                  {!searchFilters.vendorName.allHidden && (
                    <FormControlLabel
                      label={"Todos"}
                      control={
                        <Checkbox
                          id="displays_all"
                          name="displays_all"
                          checked={searchFilters.vendorName.allChecked}
                          onChange={allCheckedChangeHandler}
                        />
                      }
                    />
                  )}

                  {!searchFilters.vendorName.allHidden && searchFilters.vendorName.items.map((item: SearchFilterItem) => {
                    if (!item.visible) return null;
                    return (
                      <FormControlLabel
                        key={item.value}
                        label={item.label}
                        control={
                          <Checkbox
                            id={item.value}
                            name={item.value}
                            value={item.value}
                            checked={item.checked}
                            onChange={(event) => changeSearchFilterHandler(event, "vendorName")}
                          />
                        }
                      />
                    );
                  })}
                </div>
              </AccordionDetails>
            </Accordion>
          </div>
        )}
      </div>
    </>
  );
};
